import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import './DeleteKeyModal.css';

// Controlled React modal (Bootstrap 4 markup, no jQuery)
const DeleteKeyModal = ({
  modalId,
  keyName,
  onConfirm,
  onCancel,
  isDeleting,
  show
}) => {
  // Close on Escape, lock body scroll while open
  useEffect(() => {
    if (!show) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape' && !isDeleting) onCancel();
    };
    document.addEventListener('keydown', onKeyDown);
    document.body.classList.add('modal-open');
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.classList.remove('modal-open');
    };
  }, [show, isDeleting, onCancel]);

  if (!show) return null;

  return (
    <>
    <div
      className="modal fade show d-block"
      id={modalId}
      tabIndex="-1"
      role="dialog"
      aria-labelledby="deleteKeyModalLabel"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget && !isDeleting) onCancel();
      }}
    >
      <div className="modal-dialog modal-dialog-centered" role="document">
        <div className="modal-content delete-key-modal">
          <div className="modal-header border-0">
            <h5 className="modal-title text-danger" id="deleteKeyModalLabel">
              <i className="fa fa-exclamation-triangle" aria-hidden="true"></i> Confirm Key Deletion
            </h5>
            <button type="button" className="close" onClick={onCancel} disabled={isDeleting} aria-label="Close">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div className="modal-body">
            <div className="warning-content">
              <div className="warning-icon">
                <i className="fa fa-shield-alt text-warning fa-3x" aria-hidden="true"></i>
              </div>
              <div className="warning-text">
                <h6 className="text-danger mb-3">
                  ⚠️ This action cannot be undone!
                </h6>
                <p className="mb-3">
                  You are about to delete the encryption key: <strong>"{keyName || 'Untitled Key'}"</strong>
                </p>
                <div className="alert alert-danger">
                  <h6 className="alert-heading">
                    <i className="fa fa-lock" aria-hidden="true"></i> Critical Warning:
                  </h6>
                  <p className="mb-2">
                    <strong>If you delete this key, you will permanently lose access to all data encrypted with it.</strong>
                  </p>
                  <ul className="mb-0">
                    <li>Any SecurePaper codes created with this key will become <strong>unreadable</strong></li>
                    <li>You will <strong>never</strong> be able to decrypt this data again</li>
                    <li>This includes any physical printouts or digital copies</li>
                  </ul>
                </div>
                <p className="text-muted">
                  Only delete this key if you're absolutely certain you no longer need access to any data encrypted with it.
                </p>
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onCancel}
              disabled={isDeleting}
            >
              <i className="fa fa-times" aria-hidden="true"></i> Cancel
            </button>
            <button 
              type="button" 
              className="btn btn-danger" 
              onClick={onConfirm}
              disabled={isDeleting}
            >
              {isDeleting ? (
                <>
                  <i className="fa fa-spinner fa-spin" aria-hidden="true"></i> Deleting...
                </>
              ) : (
                <>
                  <i className="fa fa-trash" aria-hidden="true"></i> Yes, Delete Key
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
    <div className="modal-backdrop fade show"></div>
    </>
  );
};

DeleteKeyModal.propTypes = {
  modalId: PropTypes.string.isRequired,
  keyName: PropTypes.string,
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  isDeleting: PropTypes.bool,
  show: PropTypes.bool,
};

DeleteKeyModal.defaultProps = {
  keyName: 'Untitled Key',
  isDeleting: false,
  show: false,
};

export default DeleteKeyModal;
